import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useIsMobileLayout } from '@src/hooks/useIsMobileLayout';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { useUser } from '@src/hooks/useUser';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useExecutionReports } from '@src/providers/ExecutionReportsContext';
import {
  ACTION,
  BlotterTableFormattedNumberProvider,
  Box,
  bpsToPercent,
  Button,
  ButtonGroup,
  ButtonVariants,
  canExecuteOtherUserNegotiatedRFQ,
  FormControlSizes,
  HelpIcon,
  HStack,
  IconButton,
  IconName,
  IndicatorBadge,
  IndicatorBadgeSizes,
  IndicatorBadgeVariants,
  InlineFormattedNumber,
  LoaderTalos,
  ModeEnum,
  NotificationVariants,
  Popover,
  resolvePricingModeDisplaySpecs,
  SideEnum,
  Stack,
  Text,
  Toggle,
  ToggleButton,
  Tooltip,
  useCallbackRef,
  useCurrenciesContext,
  useGlobalToasts,
  useMarketsContext,
  usePopoverState,
  useSecuritiesContext,
  useSocketClient,
  type Quote,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { FillSummary } from '../Components/Quotes/PartialFillButton/PartialFillButton';
import { useRFQTradingConfirmation } from '../otherUserNegotiatedRFQTradeConfirmation';
import { RFQSweeper } from '../RFQSweeper';
import { executeRFQSweeper } from '../sweeperExecution';
import { RFQSweepingCloseRemainderButton } from './RFQSweepingCloseRemainderButton';
import { RFQSweepingPanelHeader } from './RFQSweepingPanelHeader';
import { SweeperBlotter } from './SweeperBlotter';
import { SweeperMarketsBlotter } from './SweeperMarketsBlotter';
import type { RFQSweepingPanelData } from './useRFQSweepingPanelData';

interface RFQSweepingPanelContentProps extends RFQSweepingPanelData {
  onClose: () => void;
}

// 40px AG Grid header + four 31px rows keeps a useful basket preview visible before the blotters share extra space.
const BLOTTER_MIN_HEIGHT = '164px';

export function RFQSweepingPanelContent({
  correlationID,
  quoteSweepingData,
  initialSide,
  canEditSide,
  associatedCareOrder,
  onClose,
}: RFQSweepingPanelContentProps) {
  const { isAuthorized } = useRoleAuth();
  const user = useUser();
  const { currenciesBySymbol } = useCurrenciesContext();
  const { marketDisplayNameByName } = useMarketsContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const webSocketClient = useSocketClient();
  const { add: addToast } = useGlobalToasts();
  const { executionReportSub } = useExecutionReports();
  const { allowedSlippage, rfqSweepingShowAllDecimals, setRfqSweepingShowAllDecimals } = useTradingSettings();
  const { truncateCareOrderID, rfqSweeperAutoCancelRemainingAfterExecution, enableExecutingOtherUserNegotiatedRFQ } =
    useFeatureFlag();
  const isMobileLayout = useIsMobileLayout();

  const representativeQuote = quoteSweepingData.representativeQuote;
  const [sweeper, setSweeper] = useState<RFQSweeper>();
  const currency = representativeQuote.Currency ? currenciesBySymbol.get(representativeQuote.Currency) : undefined;
  const security = representativeQuote.Symbol ? securitiesBySymbol.get(representativeQuote.Symbol) : undefined;
  const avgPxDisplaySpec = resolvePricingModeDisplaySpecs({
    pricingMode: representativeQuote.PricingMode,
    security,
  }).entryPrice;
  const canPartiallyFillAtMarkets = representativeQuote.NegotiationParameters?.PartialFill === ModeEnum.Enabled;
  const primeBestPxTooltip = canPartiallyFillAtMarkets
    ? 'Stages tradable quotes from best to worst price until the quote quantity is allocated.'
    : 'Stages whole maker quantities from best to worst price, stopping before the next maker would exceed the quote quantity.';

  const selectedMarketNames = useMemo(
    () => new Set(sweeper?.fills.map(fill => fill.quote.quoteMarket.Market)),
    [sweeper?.fills]
  );

  const lockedMarketNames = useMemo(() => sweeper?.lockedMarketNames ?? new Set<string>(), [sweeper]);

  const rfqActionDisabledReason = getRFQSweepingActionDisabledReason({
    representativeQuote,
    currentUserName: user.Name,
    canAcceptRFQ: isAuthorized(ACTION.ACCEPT_RFQ),
    enableExecutingOtherUserNegotiatedRFQ,
  });
  const confirmDisabled = (sweeper?.sweepDisabled ?? true) || rfqActionDisabledReason != null;
  const createSweeper = useCallbackRef((sweeperSide: SideEnum) => {
    return RFQSweeper.create({
      quotes: quoteSweepingData.sweeperQuotes,
      side: sweeperSide,
      currency,
      canPartiallyFillAtMarkets,
      marketDisplayNameByName,
    });
  });

  useEffect(() => {
    setSweeper(currentSweeper => {
      // Create the initial sweeper. Do it in a useEffect since we depend on quoteSweepingData which needs to load in.
      if (!currentSweeper) {
        return createSweeper(initialSide);
      }

      return currentSweeper.updateQuotes(quoteSweepingData.sweeperQuotes);
    });
  }, [createSweeper, initialSide, quoteSweepingData.sweeperQuotes]);

  const handleToggleMarket = useCallbackRef((marketName: string) => {
    setSweeper(currentSweeper =>
      currentSweeper?.fills.some(fill => fill.quote.quoteMarket.Market === marketName)
        ? currentSweeper.removeMarket(marketName)
        : currentSweeper?.addMarket(marketName)
    );
  });

  const handleRemoveMarket = useCallbackRef((marketName: string) => {
    setSweeper(currentSweeper => currentSweeper?.removeMarket(marketName));
  });

  const handleSideChange = useCallbackRef((nextSide: SideEnum) => {
    if (!canEditSide) {
      return;
    }

    setSweeper(currentSweeper => {
      if (!currentSweeper || nextSide === currentSweeper.side) {
        return currentSweeper;
      }

      return currentSweeper.updateSide(nextSide);
    });
  });

  const handleDesiredQtyChange = useCallbackRef((marketName: string, value: string) => {
    setSweeper(currentSweeper => currentSweeper?.setDesiredQty(marketName, value));
  });

  const handlePrimeBestFills = useCallback(() => {
    setSweeper(currentSweeper => currentSweeper?.primeBestFills());
  }, []);

  const handleCancel = useCallbackRef(() => {
    onClose();
  });

  const executeSweep = useCallbackRef(async () => {
    if (!representativeQuote || !sweeper || confirmDisabled) {
      return;
    }

    const security = securitiesBySymbol.get(representativeQuote.Symbol);

    if (!security) {
      addToast({
        text: `Cannot sweep RFQ: missing security details for ${representativeQuote.Symbol}.`,
        variant: NotificationVariants.Negative,
        dismissable: true,
        timeout: 5000,
      });
      return;
    }

    await executeRFQSweeper({
      sweeper,
      security,
      webSocketClient,
      executionReportsSub: executionReportSub,
      addToast,
      allowedSlippage: allowedSlippage?.value ? bpsToPercent(allowedSlippage.value) : undefined,
      truncateCareOrderID,
      associatedCareOrder,
      autoCancelRemainingAfterExecution: rfqSweeperAutoCancelRemainingAfterExecution,
      quoteSub: quoteSweepingData.quoteDataForSweepGroupObservable,
      onUpdate: setSweeper,
      isMobileLayout,
    });
  });

  const { execute: handleConfirm, dialog: tradeConfirmationDialog } = useRFQTradingConfirmation({
    quote: representativeQuote,
    execute: executeSweep,
  });

  const formattedNumberSettings = useMemo(
    () => ({
      showAllDecimals: rfqSweepingShowAllDecimals,
    }),
    [rfqSweepingShowAllDecimals]
  );
  const currentFillSummary = sweeper?.currentFillSummary;
  const autoCancelRemainingQuoteCount = sweeper?.autoCancelRemainingQuoteCount ?? 0;
  const showAutoCancelRemainingText = rfqSweeperAutoCancelRemainingAfterExecution && autoCancelRemainingQuoteCount > 0;

  return (
    <>
      {quoteSweepingData == null || sweeper == null ? (
        <LoaderTalos />
      ) : (
        <BlotterTableFormattedNumberProvider settings={formattedNumberSettings}>
          <Stack h="100%" gap="spacing1" background="backgroundModal">
            <RFQSweepingPanelHeader
              representativeQuote={quoteSweepingData.representativeQuote}
              sweeperQuotes={quoteSweepingData.sweeperQuotes}
              availableQty={sweeper.availableTradableQty}
              showAvailableQtyWarning={sweeper.hasInsufficientAvailableQty}
              suffix={
                <RFQSweepingPanelSettingsButton
                  showAllDecimals={rfqSweepingShowAllDecimals}
                  onShowAllDecimalsChange={setRfqSweepingShowAllDecimals}
                />
              }
            />

            <SectionBar w="100%" justifyContent="flex-start">
              <Text color="colorTextImportant" size="fontSizeSm">
                Side
              </Text>
              {canEditSide ? (
                <SideToggle side={sweeper.side} disabled={sweeper.hasExecutedFills} onChange={handleSideChange} />
              ) : (
                <SideBadge side={sweeper.side} />
              )}
            </SectionBar>

            <Box minHeight={BLOTTER_MIN_HEIGHT} flex={1} w="100%" data-testid="rfq-sweeping-markets-blotter">
              <SweeperMarketsBlotter
                quoteSweepingData={quoteSweepingData}
                side={sweeper.side}
                selectedMarketNames={selectedMarketNames}
                lockedMarketNames={lockedMarketNames}
                showAllDecimals={rfqSweepingShowAllDecimals}
                onToggleMarket={handleToggleMarket}
              />
            </Box>

            <SectionBar w="100%" justifyContent="space-between">
              <HStack gap="spacing8">
                <Text color="colorTextImportant" fontSize="fontSizeSm">
                  Sweeping Basket
                </Text>
                {currentFillSummary && (
                  <IndicatorBadge size={IndicatorBadgeSizes.Large}>
                    <HStack gap="spacing4" data-testid="rfq-sweeping-current-fill-summary">
                      <InlineFormattedNumber
                        number={currentFillSummary.qty}
                        currency={quoteSweepingData.representativeQuote.Currency}
                      />

                      <Text>@</Text>

                      <InlineFormattedNumber
                        number={avgPxDisplaySpec.toDisplayPrice(currentFillSummary.avgPx)}
                        currency={avgPxDisplaySpec.priceCurrency}
                        increment={avgPxDisplaySpec.priceIncrement}
                        specification={avgPxDisplaySpec.priceDisplaySpec}
                      />
                    </HStack>
                  </IndicatorBadge>
                )}
              </HStack>

              <HStack gap="spacing12">
                <Button
                  size={FormControlSizes.Small}
                  onClick={handlePrimeBestFills}
                  disabled={sweeper.loading}
                  suffix={<HelpIcon tooltip={primeBestPxTooltip} placement="top" />}
                  data-testid="rfq-sweeping-prime-best-button"
                >
                  Prime Best
                </Button>
              </HStack>
            </SectionBar>

            <Box minHeight={BLOTTER_MIN_HEIGHT} flex={1} w="100%" data-testid="rfq-sweeping-fills-blotter">
              <SweeperBlotter
                sweeper={sweeper}
                symbol={quoteSweepingData.representativeQuote.Symbol}
                pricingMode={quoteSweepingData.representativeQuote.PricingMode}
                showAllDecimals={rfqSweepingShowAllDecimals}
                onDesiredQtyChange={handleDesiredQtyChange}
                onRemoveMarket={handleRemoveMarket}
              />
            </Box>

            <SectionContent w="100%" fontSize="fontSizeSm" data-testid="rfq-sweeping-fill-summary">
              <FillSummary
                desiredQty={sweeper.totalQty ?? '0'}
                quote={quoteSweepingData.representativeQuote}
                layout="horizontal"
              />
            </SectionContent>

            <HStack px="spacing12" py="spacing12" alignItems="center" justifyContent="space-between">
              <HStack gap="spacing8">
                <Button size={FormControlSizes.Small} onClick={handleCancel}>
                  Close
                </Button>

                <RFQSweepingCloseRemainderButton
                  correlationID={correlationID}
                  quoteSweepingData={quoteSweepingData}
                  disabled={sweeper.loading || rfqActionDisabledReason != null}
                  disabledReason={rfqActionDisabledReason}
                />
              </HStack>

              <HStack gap="spacing8">
                {showAutoCancelRemainingText && (
                  <IndicatorBadge size={IndicatorBadgeSizes.Large}>
                    <HStack gap="spacing4">
                      <Text>Auto-cancel enabled</Text>
                      <HelpIcon tooltip="Upon successful execution, any remaining non-terminal quotes will be canceled automatically." />
                    </HStack>
                  </IndicatorBadge>
                )}

                <Tooltip tooltip={rfqActionDisabledReason ?? sweeper.error}>
                  <Button
                    size={FormControlSizes.Small}
                    variant={sweeper.side === SideEnum.Buy ? ButtonVariants.Positive : ButtonVariants.Negative}
                    onClick={handleConfirm}
                    disabled={confirmDisabled}
                    loading={sweeper.loading}
                    data-testid="rfq-sweeping-confirm-button"
                  >
                    Confirm {sweeper.side}
                  </Button>
                </Tooltip>
              </HStack>
            </HStack>
          </Stack>
        </BlotterTableFormattedNumberProvider>
      )}
      {tradeConfirmationDialog}
    </>
  );
}

const SectionBar = styled(HStack)`
  height: 40px;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing6}px;
  padding: 0 ${({ theme }) => theme.spacing16}px;
  border-top: 1px solid ${({ theme }) => theme.backgroundBody};
  border-bottom: 1px solid ${({ theme }) => theme.backgroundBody};
`;

const SectionContent = styled(Box)`
  padding: ${({ theme }) => theme.spacing12}px ${({ theme }) => theme.spacing16}px;
  border-top: 1px solid ${({ theme }) => theme.backgroundBody};
  border-bottom: 1px solid ${({ theme }) => theme.backgroundBody};
`;

function RFQSweepingPanelSettingsButton({
  showAllDecimals,
  onShowAllDecimalsChange,
}: {
  showAllDecimals: boolean;
  onShowAllDecimalsChange: (showAllDecimals: boolean) => void;
}) {
  const popover = usePopoverState({
    closeOnClickOutside: true,
    trigger: 'click',
    placement: 'bottom-end',
    usePortal: false,
  });

  return (
    <Popover {...popover}>
      <IconButton
        ghost
        icon={IconName.Cog}
        color="colorTextDefault"
        size={FormControlSizes.Default}
        data-testid="rfq-sweeping-settings-cog"
      />
      <Stack gap="spacing8">
        <Toggle
          size={FormControlSizes.Small}
          checked={showAllDecimals}
          onChange={onShowAllDecimalsChange}
          label="Show All Decimals"
          justifyContent="space-between"
          data-testid="rfq-sweeping-show-decimals-toggle"
        />
      </Stack>
    </Popover>
  );
}

function SideToggle({
  side,
  disabled,
  onChange,
}: {
  side: SideEnum;
  disabled: boolean;
  onChange: (side: SideEnum) => void;
}) {
  return (
    <ButtonGroup type="switch" size={FormControlSizes.Tiny}>
      <ToggleButton
        selected={side === SideEnum.Sell}
        selectedVariant={ButtonVariants.Negative}
        disabled={disabled}
        onClick={() => onChange(SideEnum.Sell)}
        data-testid="rfq-sweeping-sell-toggle"
      >
        Sell
      </ToggleButton>
      <ToggleButton
        selected={side === SideEnum.Buy}
        selectedVariant={ButtonVariants.Positive}
        disabled={disabled}
        onClick={() => onChange(SideEnum.Buy)}
        data-testid="rfq-sweeping-buy-toggle"
      >
        Buy
      </ToggleButton>
    </ButtonGroup>
  );
}

function SideBadge({ side }: { side: SideEnum }) {
  return (
    <IndicatorBadge variant={side === SideEnum.Buy ? IndicatorBadgeVariants.Positive : IndicatorBadgeVariants.Negative}>
      {side}
    </IndicatorBadge>
  );
}

function getRFQSweepingActionDisabledReason({
  representativeQuote,
  currentUserName,
  canAcceptRFQ,
  enableExecutingOtherUserNegotiatedRFQ,
}: {
  representativeQuote: Pick<Quote, 'RFQTradingProtocol' | 'User'>;
  currentUserName: string | undefined;
  canAcceptRFQ: boolean;
  enableExecutingOtherUserNegotiatedRFQ: boolean;
}): string | undefined {
  if (!canAcceptRFQ) {
    return 'You are not authorized to accept RFQs.';
  }

  const isOwnQuote = representativeQuote.User === currentUserName;
  const canTradeOtherUserNegotiatedRFQ = canExecuteOtherUserNegotiatedRFQ({
    rfqTradingProtocol: representativeQuote.RFQTradingProtocol,
    enableExecutingOtherUserNegotiatedRFQ,
  });

  if (!isOwnQuote && !canTradeOtherUserNegotiatedRFQ) {
    return 'Quote belongs to another user.';
  }

  return undefined;
}
